// Home: how your posts are doing, and the next one. Three numbers (people reached; likes,
// comments, saves, shares; clicks to your link), each split by channel, then the all-time
// leaderboard where every row can be made again. Bookings are out of our hands and not here.
import type { HomeDto, HomePeriod, LeaderboardRow } from '@str/api/types';
import { CREDIT_COST } from '@str/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HomeTile } from '@/components/home/HomeTile';
import { ErrorText } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { Leaderboard } from '@/components/home/Leaderboard';
import {
  CHANNEL_NAMES,
  loopLine,
  notInYet,
  prefillFromPost,
  usualLine,
  whenParts,
} from '@/lib/home';
import { useActiveProperty } from '@/lib/property-store';
import { useHome, useMe, usePlan } from '@/lib/queries';

const PERIODS: HomePeriod[] = [7, 30, 90];
const TILES: { key: keyof HomeDto['tiles']; label: string; live?: boolean }[] = [
  { key: 'reached', label: 'People reached' },
  { key: 'engaged', label: 'Likes, comments, saves, shares' },
  { key: 'clicks', label: 'Clicks to your link', live: true },
];

function today(): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date());
}

/** The month target on the status line: how many are planned, and the way to the rest. */
function PlanStatus({ plan }: { plan: { target: number; planned: number } | undefined }) {
  if (!plan) return null;
  if (plan.planned >= plan.target) return <> · month planned</>;
  return (
    <>
      {' '}
      · {plan.planned} of {plan.target} this month ·{' '}
      <Link to="/app/calendar" className="h-plan-link">
        plan the rest
      </Link>
    </>
  );
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function Home() {
  const { active } = useActiveProperty();
  const navigate = useNavigate();
  const me = useMe();
  const [period, setPeriod] = useState<HomePeriod>(30);
  const home = useHome(active.id, period);
  const plan = usePlan(active.id).data;
  const data = home.data;

  const make = (row: LeaderboardRow) => {
    prefillFromPost(active.id, row);
    if (row.format === 'Reel')
      void navigate('/app/reel', { state: { fresh: crypto.randomUUID(), follow: true } });
    else void navigate('/app/create', { state: { follow: true } });
  };

  if (data && data.publishedTotal === 0) {
    const credits = me.data?.credits;
    const left = credits
      ? credits.resetsAt
        ? `${credits.balance} left this month.`
        : `${credits.balance} left on your trial.`
      : '';
    // Nothing has gone out, but the page says what there is: drafts waiting, a post on the way,
    // or truly nothing yet. Only the last one is a first-post start.
    const drafts = data.draftCount ?? 0;
    const scheduled = data.scheduledCount ?? 0;
    const draftsLine = `${drafts} draft${drafts === 1 ? '' : 's'}`;
    const goesOut = data.nextScheduledAt
      ? whenParts(data.nextScheduledAt, null, data.timezone).day
      : null;
    // A slot that has come and gone without a send (no channel took it): say so, not "on its way".
    const overdue = !!data.nextScheduledAt && new Date(data.nextScheduledAt) < new Date();
    const eyebrow =
      scheduled && drafts
        ? `${scheduled} scheduled · ${draftsLine} · nothing published`
        : scheduled
          ? `${scheduled} scheduled · nothing published`
          : drafts
            ? `${draftsLine} · nothing published`
            : 'No posts yet';
    const nothingConnected = data.connected.length === 0;
    return (
      <div className="screen h-page">
        <div className="h-head">
          <span className="h-caps">{active.name} · Home</span>
        </div>
        <div className="h-empty h-rise">
          <span className="h-caps">
            {eyebrow}
            <PlanStatus plan={plan} />
          </span>
          <h1 className="h-h1">
            {scheduled && overdue
              ? 'A post is waiting on the calendar'
              : scheduled
                ? 'Your first post is on its way'
                : 'Nothing has gone out yet'}
          </h1>
          <p className="h-sub">
            {scheduled && overdue
              ? `It was due ${goesOut} and has not gone out. Open it on the calendar to pick a new time or move it back to drafts.${drafts ? ` ${draftsLine} still waiting.` : ''}`
              : scheduled
                ? `${scheduled === 1 ? 'It goes' : 'The first one goes'} out ${goesOut ?? 'soon'}.${drafts ? ` ${draftsLine} still waiting.` : ''} Once it is out, this page shows who it reached, on which channel, and what to make next.`
                : drafts
                  ? `${draftsLine} waiting. Finish one and schedule it; once it is out, this page shows who it reached, on which channel, and what to make next.`
                  : 'Your first post takes about a minute. Once it is out, this page shows who it reached, on which channel, and what to make next.'}
          </p>
          <div className="h-actions">
            {scheduled ? (
              <Link to="/app/calendar" className="btn btn-primary btn-lg hv">
                See the calendar
              </Link>
            ) : drafts ? (
              <Link to="/app/posts" state={{ tab: 'Drafts' }} className="btn btn-primary btn-lg hv">
                Pick up a draft
              </Link>
            ) : (
              <Link to="/app/create" className="btn btn-primary btn-lg hv">
                Make your first post
              </Link>
            )}
            {scheduled && drafts ? (
              <Link to="/app/posts" state={{ tab: 'Drafts' }} className="btn btn-ghost hv">
                Pick up a draft
              </Link>
            ) : null}
            {scheduled || drafts ? (
              <Link to="/app/create" className="btn btn-ghost hv">
                {scheduled ? 'Make another post' : 'Start a new post'}
              </Link>
            ) : null}
            {nothingConnected && (
              <Link to="/app/channels" className="btn btn-ghost hv">
                Connect Instagram and Facebook
              </Link>
            )}
          </div>
          <p className="h-note">
            {scheduled || drafts ? '' : 'Nothing is made yet. '}Make it, at the end, spends{' '}
            {CREDIT_COST.enhancedStill} credits a picture. {left}
          </p>
        </div>
      </div>
    );
  }

  const notConnected = data?.notConnected ?? [];
  return (
    <div className="screen h-page">
      <div className="h-head">
        <span className="h-caps">
          {active.name} · Last {period} days · {today()}
          <PlanStatus plan={plan} />
        </span>
        <Link to="/app/create" className="btn btn-primary hv">
          Make a post
        </Link>
      </div>
      <div className="h-title h-rise">
        <h1 className="h-h1">How your posts are doing</h1>
        {data ? (
          <p className="h-sub" key={period}>
            {loopLine(data)}
          </p>
        ) : (
          <span className="sk" style={{ width: 520, height: 18 }} />
        )}
      </div>
      <div className="seg round h-seg" role="radiogroup" aria-label="Period">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={period === p}
            className={`seg-item hv${period === p ? ' on' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p} days
          </button>
        ))}
      </div>
      {!data && home.isError ? (
        <div
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <ErrorText>Couldn&apos;t load your numbers. {errorMessage(home.error)}</ErrorText>
          <button type="button" className="btn-pill hv" onClick={() => void home.refetch()}>
            Try again
          </button>
        </div>
      ) : !data ? (
        <div role="status" aria-label="Loading your numbers" className="h-loading">
          <div className="h-tiles">
            {TILES.map((t) => (
              <div key={t.key} className="h-tile">
                <span className="sk" style={{ width: 140, height: 14 }} />
                <span className="sk" style={{ width: 180, height: 12 }} />
                <span className="sk" style={{ width: 120, height: 34, marginTop: 4 }} />
                <span className="sk" style={{ width: '100%', height: 34 }} />
                <span className="sk" style={{ width: 220, height: 12 }} />
              </div>
            ))}
          </div>
          <span className="sk" style={{ width: 460, height: 12 }} />
          <div className="h-board-sk">
            <span className="sk" style={{ width: 240, height: 18 }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="sk" style={{ width: '100%', height: 66 }} />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="h-tiles" key={`tiles-${period}`}>
            {TILES.map((t, i) => (
              <HomeTile
                key={t.key}
                label={t.label}
                tile={data.tiles[t.key]}
                period={period}
                live={t.live}
                notIn={notInYet(data)}
                waiting={data.pending.map((p) => p.channel)}
                delay={80 + i * 80}
              />
            ))}
          </div>
          <div className="h-note h-rise" style={{ animationDelay: '320ms' }}>
            <span>
              Channel numbers arrive up to 2 days late. Clicks are counted the moment they happen.
            </span>
            {data.pending.map((p) => (
              <span key={p.channel}>{p.reason}.</span>
            ))}
            {notConnected.length > 0 && (
              <span>
                {listNames(notConnected.map((c) => (c === 'YT' ? 'Shorts' : CHANNEL_NAMES[c])))}{' '}
                {notConnected.length === 1 ? 'is' : 'are'} not connected ·{' '}
                <Link to="/app/channels">connect</Link>
              </span>
            )}
          </div>
          <div className="h-board-head h-rise" style={{ animationDelay: '400ms' }}>
            <h2 className="h-h2">Your best posts, all time</h2>
            <span className="h-sub">
              {data.leaderboard.some((r) => r.measured)
                ? 'By people reached, every channel added up'
                : 'Not ranked yet: no channel has reported numbers'}{' '}
              ·{' '}
              <Link to="/app/posts">
                {data.publishedTotal === 1
                  ? 'On Posts too'
                  : data.publishedTotal === 2
                    ? 'Both on Posts'
                    : `All ${data.publishedTotal} on Posts`}
              </Link>
            </span>
          </div>
          <Leaderboard
            rows={data.leaderboard}
            usual={usualLine(data.usual)}
            onOpen={(r) => void navigate(`/app/posts/${r.id}/results`)}
            onMake={make}
            delay={480}
          />
          <p className="h-note h-rise" style={{ animationDelay: '560ms' }}>
            Make another like this keeps the shape, the format, the time slot and the channels,
            matches fresh photos of the same place, and asks you for the moment.
          </p>
        </>
      )}
    </div>
  );
}
